import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  MapLegend,
  RideMap,
  Sheet,
  Spinner,
  boundsAround,
  cx,
  dateOnly,
  haptic,
  useGeolocation,
  useLastUnratedRide,
  useWakeLock,
} from '@mobi/web';
import { useDriverRide, useDriverStatus } from '../useDriver.js';
import { Onboarding } from './Onboarding.js';
import { OfferModal } from '../components/OfferModal.js';
import { DriverRideView } from '../components/DriverRideView.js';
import { FinishedRide } from '../components/FinishedRide.js';
import type { DriverRide } from '../useDriver.js';

const SUPPORT_WHATSAPP_URL =
  'https://wa.me/5535998462192?text=' +
  encodeURIComponent('Oi! Quero contratar um plano de assinatura para rodar como motorista.');

export function Home() {
  const { status, loading: loadingStatus, refresh: refreshStatus } = useDriverStatus();
  const [online, setOnline] = useState(false);
  const [goingOnline, setGoingOnline] = useState(false);
  const [onlineError, setOnlineError] = useState<string | null>(null);
  const [justFinished, setJustFinished] = useState<DriverRide | null>(null);

  const {
    socket,
    connected,
    ride,
    offer,
    passengerPosition,
    loading: loadingRide,
    refresh: refreshRide,
    dismissOffer,
  } = useDriverRide(online);

  // GPS ligado enquanto online OU em corrida: é o que alimenta o mapa do
  // passageiro. Desligado fora disso, para não gastar bateria à toa.
  // Rede de segurança da avaliação: `justFinished` some se o motorista
  // fechar o app, e com ele sumiam a nota do passageiro e o valor a
  // receber. Isto traz a corrida de volta na reabertura.
  const {
    pending: unrated,
    recheck: recheckRating,
    dismiss: dismissRating,
  } = useLastUnratedRide<DriverRide>(ride?.id ?? null);

  const geo = useGeolocation(online || ride !== null);
  const wake = useWakeLock(online || ride !== null);

  const myPosition = geo.status === 'ok' ? geo.coords : null;

  /** Entrar no pool é ato explícito e o servidor pode recusar. */
  const goOnline = useCallback(async () => {
    if (!socket) return;
    setGoingOnline(true);
    setOnlineError(null);
    socket.emit('driver:online', {}, (ack: { ok: boolean; error?: string }) => {
      setGoingOnline(false);
      if (ack?.ok) {
        setOnline(true);
        haptic([10, 40, 10]);
      } else {
        setOnline(false);
        setOnlineError(ack?.error ?? 'não foi possível ficar online');
        void refreshStatus();
      }
    });
  }, [socket, refreshStatus]);

  const goOffline = useCallback(() => {
    socket?.emit('driver:offline', {});
    setOnline(false);
    haptic();
  }, [socket]);

  // Reconectou: o índice do servidor esvaziou junto com o socket antigo.
  // Sem reanunciar, o motorista fica "online" na tela e invisível de fato.
  useEffect(() => {
    if (connected && online) void goOnline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected]);

  // Corrida acabou de terminar: reanuncia online sozinho, sem esperar o
  // motorista clicar de novo. `online` (client) nunca vira false durante
  // a corrida, mas o índice do SERVIDOR pode ter ficado dessincronizado
  // no meio do caminho (uma reconexão breve que o efeito acima não pegou
  // a tempo); reemitir aqui fecha essa janela em vez de depender só dela.
  const hadRide = useRef(false);
  useEffect(() => {
    if (ride) {
      hadRide.current = true;
      return;
    }
    if (hadRide.current && online) void goOnline();
    hadRide.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ride]);

  // Transmite posição enquanto online. Quem filtra posição velha é o
  // servidor; aqui só mandamos no ritmo combinado.
  useEffect(() => {
    if (!socket || !online || geo.status !== 'ok') return;
    const send = () =>
      socket.emit('driver:position', {
        lat: geo.coords.lat,
        lng: geo.coords.lng,
        ...(geo.coords.heading !== undefined ? { heading: geo.coords.heading } : {}),
      });
    send();
    const timer = setInterval(send, 4_000);
    return () => clearInterval(timer);
  }, [socket, online, geo]);

  if (loadingStatus || loadingRide) {
    return (
      <div className="grid h-full place-items-center text-brand">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!status) return null;

  const cadastroIncompleto =
    status.approvalStatus !== 'approved' || status.missing.length > 0;

  if (cadastroIncompleto) {
    return <Onboarding status={status} onChanged={refreshStatus} />;
  }

  // O POST de encerramento traz nome do passageiro e valores; a
  // listagem traz o essencial. Prefere-se o primeiro quando existe.
  const finished = justFinished ?? unrated;

  if (finished) {
    return (
      <FinishedRide
        ride={finished}
        onDone={() => {
          setJustFinished(null);
          dismissRating(finished.id);
          void recheckRating();
          void refreshRide();
        }}
      />
    );
  }

  if (ride) {
    return (
      <>
        <DriverRideView
          ride={ride}
          passengerPosition={passengerPosition}
          myPosition={myPosition}
          vehicleType={status.vehicle?.type}
          socket={socket}
          onChanged={refreshRide}
          onFinished={(result) => setJustFinished({ ...ride, ...result, status: 'finished' })}
        />
        {offer && <OfferModal offer={offer} onResolved={dismissOffer} />}
      </>
    );
  }

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="relative min-h-0 flex-1">
          <RideMap
            className="absolute inset-0"
            bounds={myPosition ? boundsAround(myPosition, 2_000) : undefined}
            markers={
              myPosition
                ? [
                    {
                      point: myPosition,
                      kind: 'driver' as const,
                      vehicleType: status.vehicle?.type,
                      selfDriver: true,
                    },
                  ]
                : []
            }
          />
          <div className="pt-safe absolute inset-x-0 top-0 px-4">
            <div className="elevated-lg rounded-3xl border border-line/60 bg-surface/85 px-4 py-3 backdrop-blur-xl">
              <div className="flex items-center justify-between gap-3">
                <span
                  className={cx(
                    'inline-flex items-center gap-2 text-sm font-semibold',
                    online ? 'text-good' : 'text-faint',
                  )}
                >
                  <span className="relative flex h-2.5 w-2.5">
                    {online && (
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-good opacity-75" />
                    )}
                    <span
                      className={cx(
                        'relative inline-block h-2.5 w-2.5 rounded-full',
                        online ? 'bg-good' : 'bg-faint',
                      )}
                    />
                  </span>
                  {online ? 'Recebendo corridas' : 'Offline'}
                </span>
                {!connected && <Badge tone="warn">reconectando</Badge>}
              </div>
              <div className="mt-2">
                <MapLegend items={[{ kind: 'driver', label: 'Você' }]} />
              </div>
            </div>
          </div>
        </div>

        <Sheet className="max-h-[62%] overflow-y-auto">
          {status.subscriptionWarning && (
            <div className="mb-4 rounded-2xl bg-warn/10 px-4 py-3">
              <p className="text-sm text-warn">{status.subscriptionWarning}</p>
              {!status.subscription && (
                <a
                  href={SUPPORT_WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-[#25D366] px-4 text-sm font-semibold text-white"
                >
                  Contratar plano no WhatsApp
                </a>
              )}
            </div>
          )}

          {status.subscription && (
            <p className="mb-3 text-sm text-muted">
              Plano {status.subscription.planName} · válido até{' '}
              {dateOnly(status.subscription.expiresOn)}
            </p>
          )}

          {geo.status === 'denied' && (
            <div className="mb-4 rounded-2xl bg-danger/10 px-4 py-3 text-sm text-danger">
              Sem permissão de localização, você não recebe corridas. Libere o GPS nas
              configurações do navegador.
            </div>
          )}

          {onlineError && (
            <div className="mb-4 rounded-2xl bg-danger/10 px-4 py-3 text-sm text-danger">
              {onlineError}
            </div>
          )}

          {online && wake.supported && !wake.active && (
            <div className="mb-4 rounded-2xl bg-warn/10 px-4 py-3 text-sm text-warn">
              A tela pode apagar. Mantenha o app aberto e o celular no suporte — com a tela
              apagada, o GPS para e o passageiro deixa de ver você.
            </div>
          )}

          {online && !wake.supported && (
            <div className="mb-4 rounded-2xl bg-warn/10 px-4 py-3 text-sm text-warn">
              Este navegador não mantém a tela acesa sozinho. Deixe o app aberto e a tela ligada
              enquanto estiver online.
            </div>
          )}

          <Button
            size="lg"
            full
            loading={goingOnline}
            variant={online ? 'outline' : 'primary'}
            onClick={() => (online ? goOffline() : void goOnline())}
          >
            {online ? 'Ficar offline' : 'Ficar online'}
          </Button>

          {!online && (
            <p className="mt-3 text-center text-sm text-faint">
              Enquanto estiver online, mantenha o app na frente e o celular no suporte.
            </p>
          )}
        </Sheet>
      </div>

      {offer && <OfferModal offer={offer} onResolved={dismissOffer} />}
    </>
  );
}
